'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState, useMemo } from 'react';

import { FeedbackFormLink } from './FeedbackFormLink';
import { NavigationLink } from './NavigationLink';
import navigationLinks from './navigationLinks';
import { HeaderProps } from './types';
import { Search } from '../../search';

import IconChevron from '@/assets/icons/iconChevron.svg';
import IconClose from '@/assets/icons/iconClose.svg';
import IconLogo from '@/assets/icons/iconLogo.svg';
import IconLogoEng from '@/assets/icons/iconLogoEng.svg';
import IconMenu from '@/assets/icons/iconMenu.svg';
import IconSearch from '@/assets/icons/iconSearch.svg';
import { Button } from '@/shared/components/button';
import { LanguageSelector } from '@/shared/components/languageSelector';
import { Link } from '@/shared/components/link';
import { ScrollButton } from '@/shared/components/scrollButton';
import { SCREEN_SM } from '@/shared/constants/common';
import { classNames } from '@/shared/lib/classNames/classNames';
import smoothScroll from '@/shared/utils/smoothScroll';

export const Header = ({ currentLanguage, translations }: HeaderProps) => {
  const navigationRef = useRef<HTMLDivElement>(null);
  const [isNavigationVisible, setIsNavigationVisible] = useState(true);
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const [isUserScrollingUp, setIsUserScrollingUp] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>();

  const pathname = usePathname();
  const needToShowCloseButton = pathname?.includes('/search');
  const needToShowPlatformManagerSignInButton =
    pathname?.includes('/platform-manager');

  const translatedNavigationLinks = useMemo(
    () =>
      navigationLinks
        .map(({ label, subLinks, href, ...rest }) => ({
          ...rest,
          label: translations[label],
          href: `${translations[href] || href}`,
          subLinks: subLinks?.map(({ label, href, ...rest }) => ({
            label: translations[label],
            href: `${translations[href] || href}`,
            ...rest,
          })),
        }))
        .filter(({ label }) => label),
    [translations],
  );

  useEffect(() => {
    setIsMobile(window.innerWidth <= SCREEN_SM);

    const handleResize = () => setIsMobile(window.innerWidth <= SCREEN_SM);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let previousScrollY = window.scrollY;

    const updateScrollDirection = () => {
      const currentScrollY = window.scrollY;
      const windowBottom = window.innerHeight + currentScrollY;
      const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
      );

      if (currentScrollY < 250) {
        setIsUserScrollingUp(false);
        return;
      }

      if (windowBottom >= docHeight - 2) {
        setIsUserScrollingUp(false);
        previousScrollY = currentScrollY;

        return;
      }

      setIsUserScrollingUp(currentScrollY < previousScrollY);
      previousScrollY = currentScrollY;
    };

    window.addEventListener('scroll', updateScrollDirection);

    return () => window.removeEventListener('scroll', updateScrollDirection);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) =>
      setIsNavigationVisible(entry.isIntersecting),
    );

    navigationRef.current && observer.observe(navigationRef.current);

    return () => {
      navigationRef.current && observer.unobserve(navigationRef.current);
    };
  }, [isMobile]);

  useEffect(() => {
    const handleDocumentClick = (event: Event) => {
      if (!navigationRef.current?.contains(event.target as Node)) {
        setIsNavigationOpen(false);
      }
    };

    if (isNavigationOpen && !isMobile) {
      document.addEventListener('click', handleDocumentClick);

      const html = document.querySelector('html');

      if (html) {
        html.style.overflow = 'hidden';
      }
    }

    if (!isNavigationOpen) {
      const html = document.querySelector('html');

      if (html) {
        html.style.overflow = 'initial';
        html.style.overflowX = 'clip';
      }
    }

    return () => document.removeEventListener('click', handleDocumentClick);
  }, [isNavigationOpen, isMobile]);

  const handleToggleNavigation = () => {
    setIsNavigationOpen((isNavigationOpen) => !isNavigationOpen);
  };

  return (
    <>
      <header
        className={classNames(
          'relative z-[100] mb-9 box-content h-40 pt-6 max-sm:fixed max-sm:top-0 max-sm:mx-0 max-sm:mb-0 max-sm:h-auto max-sm:w-full max-sm:p-0',
          { 'max-sm:h-full max-sm:overflow-auto': isNavigationOpen },
        )}
      >
        <div
          className={classNames(
            'z-[100] mx-auto flex w-full max-w-[calc(min(100vw-8px,1168px))] items-center justify-end pb-6 max-sm:h-16 max-sm:rounded-none max-sm:bg-surfaceBaseSecondary max-sm:px-4 max-sm:py-2',
            {
              'fixed left-1/2 top-0 z-[100] w-full -translate-x-1/2 py-4 backdrop-blur-[16px]':
                isUserScrollingUp && !isMobile,
              'bg-[#192025EB] shadow-[0px_-15px_15px_13px_#192025]':
                isUserScrollingUp && !isMobile && !isNavigationOpen,
            },
          )}
        >
          <Link className="float-left mr-auto" href="/" labelLink="logo">
            {currentLanguage === 'ru' ? (
              <IconLogo
                className={classNames('h-14 max-sm:h-11', {
                  '!h-12':
                    !isNavigationVisible && isUserScrollingUp && !isMobile,
                })}
              />
            ) : (
              <IconLogoEng
                className={classNames('h-14 max-sm:h-11', {
                  '!h-12':
                    !isNavigationVisible && isUserScrollingUp && !isMobile,
                })}
              />
            )}
          </Link>
          {!isMobile && (
            <Search
              placeholder={translations.headerSearchPlaceholder}
              notFound={translations.headerSearchNotFound}
              lang={currentLanguage}
              showFullResults={translations.headerShowAllResults}
            />
          )}
          <LanguageSelector
            currentLanguage={currentLanguage}
            className="shrink-0"
            customClassName={{
              contentWrapper:
                'max-sm:!bg-surfaceBaseBlur max-sm:!backdrop-blur-[16px]',
              contentItem: 'flex justify-between items-center pl-2 pr-2',
            }}
            isMobile={isMobile}
          />

          {!needToShowPlatformManagerSignInButton && (
            <>
              <ScrollButton
                className="flex shrink-0 justify-center px-7 max-sm:hidden lg:ml-8"
                variant="secondary"
                target="contacts"
              >
                {translations.headerHeaderTopContactsTitle}
              </ScrollButton>
              <ScrollButton
                className="ml-3 flex shrink-0 justify-center px-7 max-sm:hidden max-sm:w-24"
                variant="primary"
                target="form"
              >
                {isMobile ? (
                  <span>Заявка</span>
                ) : (
                  <span>{translations.headerHeaderTopApplyTitle}</span>
                )}
              </ScrollButton>
            </>
          )}

          {needToShowPlatformManagerSignInButton && (
            <Button
              variant="primary"
              className="ml-3 px-4 max-sm:hidden"
              href="https://platform.2gis.ru/?utm_source=dev2gis&utm_medium=header&utm_campaign=landingPLM "
              target="_blank"
            >
              {translations.headerMenuSignInButton}
            </Button>
          )}

          {isMobile && (
            <>
              {needToShowCloseButton && (
                <Link href="/">
                  <Button
                    className="ml-3 flex h-9 w-9 items-center justify-center"
                    ariaLabel="close"
                  >
                    <IconClose className="h-8 w-8 text-textBasePrimary" />
                  </Button>
                </Link>
              )}

              {!needToShowCloseButton && (
                <>
                  {isNavigationOpen && (
                    <Link
                      href="/search"
                      className="flex h-6 w-6 items-center justify-center"
                    >
                      <IconSearch
                        className="h-5 w-5 text-textBasePrimary"
                        onClick={handleToggleNavigation}
                      />
                    </Link>
                  )}

                  <Button
                    className="ml-3 flex h-9 w-9 items-center justify-center"
                    onClick={handleToggleNavigation}
                    ariaLabel="menu"
                  >
                    {isNavigationOpen ? (
                      <IconClose className="h-8 w-8 text-textBasePrimary" />
                    ) : (
                      <IconMenu />
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </div>

        {!isMobile && (
          <div
            ref={navigationRef}
            className={classNames(
              'mx-auto max-w-[calc(min(100vw-8px,1168px))]',
              {
                'fixed left-1/2 top-[96px] z-[100] z-[22] w-full -translate-x-1/2 rounded-b-2xl bg-surfaceBasePrimary backdrop-blur-[16px]':
                  isUserScrollingUp && !isMobile,
                relative: !isUserScrollingUp,
                'bg-[#192025EB] shadow-[0px_-4px_13px_13px_#192025]':
                  isUserScrollingUp && !isMobile && !isNavigationOpen,
                'rounded-2xl': isNavigationOpen,
              },
            )}
          >
            <nav
              itemScope
              itemType="http://schema.org/SiteNavigationElement"
              className="flex h-20 items-center justify-between rounded-2xl border-[1px] border-[#FFFFFF1F] bg-surfaceBaseSecondary px-7 py-6"
            >
              <meta itemProp="name" content="Навигационное меню" />
              <div className="flex gap-4 md:gap-8">
                <div
                  className="flex cursor-pointer md:gap-2"
                  onClick={handleToggleNavigation}
                >
                  {translations.headerMenuSectionsAllProductsTitle}
                  <IconChevron
                    className={classNames('', {
                      'rotate-180': isNavigationOpen,
                    })}
                  />
                </div>
                <Link href="/business">
                  {translations.headerMenuSectionsBusinessTitle}
                </Link>
                <Link href="/government">
                  {translations.headerMenuSectionsGovernmentTitle}
                </Link>
                <Link href="/partners">
                  {translations.headerMenuSectionsPartnersTitle}
                </Link>
              </div>
              <div className="flex cursor-pointer gap-4 md:gap-8">
                <Link href="/company">
                  {translations.headerMenuSectionsCompanyTitle}
                </Link>
                {translations.headerMenuSectionsCasesTitle && (
                  <Link href="/cases">
                    {translations.headerMenuSectionsCasesTitle}
                  </Link>
                )}
              </div>
            </nav>
            {isNavigationOpen && (
              <nav
                className="absolute z-[10] mt-4 flex w-[1168px] justify-between rounded-2xl bg-surfaceBaseSecondary p-7 shadow-[0px_2px_12px_0px_#192025CC]"
                itemScope
                itemType="http://schema.org/SiteNavigationElement"
              >
                <meta
                  itemProp="name"
                  content="Навигационное меню продукты 2Gis"
                />
                <ul className="flex h-48 flex-col flex-wrap gap-x-2">
                  {translatedNavigationLinks.map((link, index) => (
                    <NavigationLink {...link} key={index} />
                  ))}
                </ul>
                <FeedbackFormLink
                  title={translations.headerMenuSectionsCustomSectionText}
                  linkLabel={
                    translations.headerMenuSectionsCustomSectionButtonName
                  }
                  onLinkClick={() => smoothScroll('form')}
                />
              </nav>
            )}
          </div>
        )}
        {isMobile && isNavigationOpen && (
          <nav
            className="absolute top-16 z-[100] hidden w-full flex-col rounded-b-xl bg-surfaceBaseSecondary px-5 py-10 max-sm:flex"
            onClick={handleToggleNavigation}
          >
            <span className="mb-2 flex h-10 items-center text-xl leading-6">
              {translations.headerMenuSectionsAllProductsTitle}
            </span>
            <ul className="mb-5 flex flex-col gap-3">
              {translatedNavigationLinks.map((link, index) => (
                <NavigationLink {...link} key={index} />
              ))}
            </ul>
            <FeedbackFormLink
              title={translations.headerMenuSectionsCustomSectionText}
              linkLabel={translations.headerMenuSectionsCustomSectionButtonName}
              onLinkClick={() => {
                setIsNavigationOpen(false);
                smoothScroll('form');
              }}
            />
            <Link
              className="mt-4 flex h-12 items-center text-xl leading-6 text-white"
              href="/business"
            >
              {translations.headerMenuSectionsBusinessTitle}
            </Link>
            <Link
              className="flex h-12 items-center text-xl leading-6 text-white"
              href="/government"
            >
              {translations.headerMenuSectionsGovernmentTitle}
            </Link>
            <Link
              className="mb-8 flex h-12 items-center text-xl leading-6 text-white"
              href="/partners"
            >
              {translations.headerMenuSectionsPartnersTitle}
            </Link>
            <Link
              className="flex h-12 items-center text-base leading-6"
              href="/company"
            >
              {translations.headerMenuSectionsCompanyTitle}
            </Link>
            {translations.headerMenuSectionsCasesTitle && (
              <Link
                className="flex h-12 items-center text-base leading-6"
                href="/cases"
              >
                {translations.headerMenuSectionsCasesTitle}
              </Link>
            )}
          </nav>
        )}
      </header>

      {isNavigationOpen && isNavigationVisible && (
        <div
          onClick={() => setIsNavigationOpen(false)}
          className="fixed left-0 top-0 z-[30] h-[100vh] w-full bg-[#000000E0]"
        ></div>
      )}
    </>
  );
};
